import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { GeoLocationValue } from '@models/geo-location.model';
import { ArcgisMapService } from '@shared/services/arcgis-map.service';
import * as L from 'leaflet';

type GeoLocationMapContext = 'modal' | 'preview';
type GeoLocationPickerVariant = 'card' | 'plain';

@Component({
  standalone: false,
  selector: 'app-geo-location-picker',
  templateUrl: './geo-location-picker.component.html',
  styleUrls: ['./geo-location-picker.component.scss'],
})
export class GeoLocationPickerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() value: GeoLocationValue | null = null;
  @Input() disabled = false;
  @Input() label = 'Localizacion';
  @Input() showSummary = true;
  @Input() variant: GeoLocationPickerVariant = 'card';

  @Output() valueChange = new EventEmitter<GeoLocationValue | null>();

  @ViewChild('previewMapContainer')
  set previewMapContainerRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this._previewMapContainerRef = ref;
    if (ref) {
      this.schedulePreviewRender();
    }
  }
  @ViewChild('mapContainer') mapContainerRef?: ElementRef<HTMLDivElement>;
  @ViewChild('overlayRoot') overlayRootRef?: ElementRef<HTMLDivElement>;

  mapOpen = false;
  searchText = '';
  searching = false;
  statusMessage = '';
  mapLoadError = false;
  previewMapLoadError = false;
  selectedLocation: GeoLocationValue | null = null;

  private map: L.Map | null = null;
  private marker: L.Marker | null = null;
  private previewMap: L.Map | null = null;
  private previewMarker: L.Marker | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private previewResizeObserver: ResizeObserver | null = null;
  private _previewMapContainerRef?: ElementRef<HTMLDivElement>;
  private mapTileErrorLogged = false;
  private previewTileErrorLogged = false;

  constructor(
    private arcgisMapService: ArcgisMapService,
    private zone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    if (this.overlayRootRef) {
      document.body.appendChild(this.overlayRootRef.nativeElement);
    }
    this.schedulePreviewRender();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.selectedLocation = this.value;
      this.schedulePreviewRender();
    }
  }

  ngOnDestroy(): void {
    this.destroyMap();
    this.destroyPreviewMap();
    this.overlayRootRef?.nativeElement.remove();
  }

  get hasLocation(): boolean {
    return !!this.value;
  }

  get locationSummary(): string {
    return this.value ? `${this.value.lat.toFixed(5)}, ${this.value.lng.toFixed(5)}` : '-';
  }

  get actionLabel(): string {
    return this.hasLocation ? 'Editar ubicación' : 'Seleccionar ubicación';
  }

  get selectedLocationSummary(): string {
    const location = this.selectedLocation ?? this.value;
    return location ? `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}` : '-';
  }

  openMap(): void {
    if (this.disabled) return;
    this.mapOpen = true;
    this.searchText = '';
    this.statusMessage = '';
    this.selectedLocation = this.value;
    setTimeout(() => this.initMap(), 0);
  }

  closeMap(): void {
    this.destroyMap();
    this.mapOpen = false;
  }

  useMyLocation(): void {
    if (!navigator.geolocation) {
      this.statusMessage = 'Tu navegador no permite geolocalizacion.';
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => this.centerOn(position.coords.latitude, position.coords.longitude, 16),
      () => { this.statusMessage = 'No se pudo obtener tu ubicacion.'; },
    );
  }

  searchAddress(): void {
    const query = this.searchText.trim();
    if (!query || !this.map) return;

    const coordinates = this.arcgisMapService.parseCoordinates(query);
    if (coordinates) {
      this.statusMessage = '';
      this.centerOn(coordinates.lat, coordinates.lng, 16);
      return;
    }

    this.searching = true;
    this.statusMessage = '';

    this.arcgisMapService.searchAddress(query).subscribe({
      next: (results) => {
        this.searching = false;
        if (!results.length) {
          this.statusMessage = 'No se encontro esa direccion.';
          return;
        }
        this.centerOn(results[0].lat, results[0].lng, 16);
      },
      error: () => {
        this.searching = false;
        this.statusMessage = 'No se pudo buscar la direccion ahora mismo.';
      },
    });
  }

  save(): void {
    if (!this.selectedLocation) {
      this.statusMessage = 'Selecciona una ubicacion.';
      return;
    }

    const location = this.arcgisMapService.normalizeCoordinate(this.selectedLocation);
    this.value = location;
    this.valueChange.emit(location);
    this.schedulePreviewRender();
    this.closeMap();
  }

  clearLocation(): void {
    this.value = null;
    this.selectedLocation = null;
    this.valueChange.emit(null);
    this.destroyPreviewMap();
    this.closeMap();
  }

  private bindMarkerPopup(): void {
    if (!this.marker || !this.value || this.disabled) return;

    this.marker.bindPopup(`
      <button class="geo-location-popup-clear" type="button">
        Quitar ubicacion
      </button>
    `);

    this.marker.on('popupopen', (event: L.PopupEvent) => {
      const element = event.popup.getElement()?.querySelector<HTMLButtonElement>('.geo-location-popup-clear');
      element?.addEventListener('click', () => {
        this.zone.run(() => this.clearLocation());
      }, { once: true });
    });
  }

  private initMap(): void {
    if (!this.mapContainerRef) return;

    this.mapLoadError = false;
    this.mapTileErrorLogged = false;
    const start = this.startCenter();
    this.map = L.map(this.mapContainerRef.nativeElement, {
      center: [start.lat, start.lng],
      zoom: start.zoom,
      ...this.arcgisMapService.getModalZoomOptions('withoutZoomRestrictions'),
    });

    this.createSatelliteLayer('modal').addTo(this.map);
    this.marker = L.marker([start.lat, start.lng], {
      icon: this.createMarkerIcon(),
      draggable: !this.disabled,
    }).addTo(this.map);

    if (this.value) {
      this.bindMarkerPopup();
      this.setSelectedLocation(start.lat, start.lng);
    }

    this.map.on('click', (event: L.LeafletMouseEvent) => {
      if (this.disabled) return;
      this.zone.run(() => {
        this.centerOn(event.latlng.lat, event.latlng.lng, this.map?.getZoom() ?? 16);
      });
    });

    this.marker.on('dragend', () => {
      const point = this.marker?.getLatLng();
      if (!point) return;
      this.zone.run(() => {
        this.setSelectedLocation(point.lat, point.lng);
      });
    });

    this.map.invalidateSize();
    this.fixMapSize();
    this.observeContainerResize();
  }

  private renderPreviewMap(retryCount = 0): void {
    const previewContainer = this._previewMapContainerRef;
    if (!this.value || !previewContainer) {
      this.destroyPreviewMap();
      return;
    }

    const rect = previewContainer.nativeElement.getBoundingClientRect();
    if ((rect.width === 0 || rect.height === 0) && retryCount < 6) {
      setTimeout(() => this.renderPreviewMap(retryCount + 1), 100);
      return;
    }

    if (!this.previewMap) {
      this.previewMapLoadError = false;
      this.previewTileErrorLogged = false;
      this.observePreviewContainerResize(previewContainer.nativeElement);
      this.previewMap = L.map(previewContainer.nativeElement, {
        center: [this.value.lat, this.value.lng],
        zoom: 16,
        attributionControl: false,
        boxZoom: false,
        doubleClickZoom: false,
        dragging: false,
        keyboard: false,
        scrollWheelZoom: false,
        touchZoom: false,
        zoomControl: false,
      });
      this.createSatelliteLayer('preview').addTo(this.previewMap);
    }

    this.previewMarker?.remove();
    this.previewMarker = L.marker([this.value.lat, this.value.lng], {
      icon: this.createMarkerIcon('preview'),
    }).addTo(this.previewMap);

    this.previewMap.setView([this.value.lat, this.value.lng], 16);
    this.previewMap.invalidateSize();
    setTimeout(() => this.previewMap?.invalidateSize(), 150);
  }

  private createSatelliteLayer(context: GeoLocationMapContext): L.TileLayer {
    const layer = L.tileLayer(this.arcgisMapService.getSatelliteTileUrl(), {
      attribution: 'Tiles Esri',
      maxZoom: 19,
    });

    layer.on('tileerror', (event) => this.handleTileError(context, event));
    return layer;
  }

  private createMarkerIcon(mode: 'modal' | 'preview' = 'modal'): L.DivIcon {
    const size = mode === 'preview' ? 24 : 30;
    return L.divIcon({
      className: `geo-location-marker-icon geo-location-marker-icon--${mode}`,
      html: '📍',
      iconSize: [size, size],
      iconAnchor: [size / 2, size - 2],
    });
  }

  private centerOn(lat: number, lng: number, zoom: number): void {
    if (!this.map) return;
    this.map.setView([lat, lng], zoom);
    this.marker?.setLatLng([lat, lng]);
    this.setSelectedLocation(lat, lng);
  }

  private setSelectedLocation(lat: number, lng: number): void {
    this.selectedLocation = this.arcgisMapService.normalizeCoordinate({ lat, lng });
  }

  private startCenter(): { lat: number; lng: number; zoom: number } {
    if (this.value) {
      return { lat: this.value.lat, lng: this.value.lng, zoom: 16 };
    }

    return this.arcgisMapService.getDefaultMapView();
  }

  private schedulePreviewRender(): void {
    requestAnimationFrame(() => this.renderPreviewMap());
    setTimeout(() => this.renderPreviewMap(), 150);
  }

  private fixMapSize(): void {
    if (!this.map) return;
    const recalc = () => this.map?.invalidateSize();
    requestAnimationFrame(recalc);
    setTimeout(recalc, 150);
    setTimeout(recalc, 400);
  }

  private observeContainerResize(): void {
    if (!this.mapContainerRef || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => {
      this.map?.invalidateSize();
    });
    this.resizeObserver.observe(this.mapContainerRef.nativeElement);
  }

  private observePreviewContainerResize(container: HTMLDivElement): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.previewResizeObserver?.disconnect();
    this.previewResizeObserver = new ResizeObserver(() => {
      this.previewMap?.invalidateSize();
    });
    this.previewResizeObserver.observe(container);
  }

  private destroyMap(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.map) {
      this.map.off();
      this.map.remove();
    }
    this.map = null;
    this.marker = null;
    this.mapLoadError = false;
    this.mapTileErrorLogged = false;
  }

  private destroyPreviewMap(): void {
    this.previewResizeObserver?.disconnect();
    this.previewResizeObserver = null;
    if (this.previewMap) {
      this.previewMap.off();
      this.previewMap.remove();
    }
    this.previewMap = null;
    this.previewMarker = null;
    this.previewMapLoadError = false;
    this.previewTileErrorLogged = false;
  }

  private handleTileError(context: GeoLocationMapContext, event: unknown): void {
    this.zone.run(() => {
      if (context === 'preview') {
        this.previewMapLoadError = true;
      } else {
        this.mapLoadError = true;
      }
    });

    const alreadyLogged = context === 'preview' ? this.previewTileErrorLogged : this.mapTileErrorLogged;
    if (alreadyLogged) return;

    if (context === 'preview') {
      this.previewTileErrorLogged = true;
    } else {
      this.mapTileErrorLogged = true;
    }

    console.error(
      '[ArcGIS] No se pudieron cargar las teselas del mapa. ' +
      'Revisa environment.arcgis.apiKey, permisos de Mapas base, dominios autorizados o si la API key fue revocada.',
      event,
    );
  }
}
